import { useEffect, useState } from "react";
import { Send, Heart, History, Check, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Parent-only composer for sending a banner note to a child's board.
 *
 * Owner-only by design — even helpers with edit_tiles permission cannot send.
 * Lives inside Settings (PIN-gated) so the child can never accidentally open
 * the composer from kid mode.
 */
const PRESETS = [
  "I love you",
  "I miss you",
  "I'll pick you up after school",
  "Almost home",
  "You're doing amazing",
  "Daddy's thinking of you",
  "Mommy's thinking of you",
  "Be brave today",
];

export default function NotesForChild({ profileId, profileName }) {
  const [text, setText] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sentOk, setSentOk] = useState(false);
  const [error, setError] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = async () => {
    if (!profileId) return;
    try {
      const { data } = await api.get(`/profiles/${profileId}/notes/history?limit=20`);
      setHistory(data?.items || []);
    } catch (e) {
      console.error("[NotesForChild] history load failed", e);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const send = async (msg) => {
    const message = (msg || text).trim();
    if (!message) {
      setError("Please type a note first.");
      return;
    }
    if (message.length > 200) {
      setError("Notes must be 200 characters or fewer.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      await api.post(`/profiles/${profileId}/notes`, { message });
      setSentOk(true);
      setText("");
      setTimeout(() => setSentOk(false), 2500);
      loadHistory();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="brutal-card bg-[color:var(--cream-deep)] p-5"
      data-testid="notes-for-child"
    >
      <div className="flex items-center gap-2 mb-2">
        <Heart size={20} className="text-[color:var(--coral)]" fill="currentColor" />
        <h3 className="display text-xl font-extrabold">
          Send a note to {profileName || "your child"}
        </h3>
      </div>
      <p className="text-sm text-[color:var(--ink-soft)] mb-4">
        A heart banner appears on their board. They tap, the message reads aloud, then it disappears.
        No reply, no notification — perfect for school hours.
      </p>

      {/* Preset chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="notes-presets">
        {PRESETS.map((p) => (
          <button
            type="button"
            key={p}
            disabled={busy}
            onClick={() => send(p)}
            className="pill-btn text-sm"
            data-testid={`notes-preset-${p.replace(/\s+/g, "-").toLowerCase()}`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Custom text */}
      <div className="mb-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Or type your own short note (200 characters max)…"
          maxLength={200}
          rows={2}
          className="brutal-input w-full resize-none"
          data-testid="notes-custom-input"
        />
        <div className="text-[11px] text-[color:var(--ink-soft)] text-right mt-1">
          {text.length}/200
        </div>
      </div>

      {error && (
        <div
          className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-2 mb-3 text-sm flex items-center gap-2"
          data-testid="notes-error"
        >
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {sentOk && (
        <div
          className="brutal-card bg-[#D6F5C8] p-2 mb-3 text-sm flex items-center gap-2"
          data-testid="notes-sent-ok"
        >
          <Check size={14} /> Sent. They'll see it next time they open the board. 💛
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => send()}
          disabled={busy || !text.trim()}
          className="pill-btn primary flex-1"
          data-testid="notes-send"
        >
          <Send size={14} /> {busy ? "Sending…" : "Send custom note"}
        </button>
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          className="pill-btn"
          data-testid="notes-history-toggle"
        >
          <History size={14} /> History
        </button>
      </div>

      {showHistory && (
        <div className="mt-4 border-t-[2px] border-dashed border-[color:var(--ink)]/30 pt-3">
          <div className="display text-xs uppercase tracking-[0.2em] font-bold text-[color:var(--ink-soft)] mb-2">
            Recently sent
          </div>
          {history.length === 0 ? (
            <div className="text-sm text-[color:var(--ink-soft)] italic">
              No notes sent yet. Send your first one above.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="notes-history-list">
              {history.map((n) => (
                <li
                  key={n.id}
                  className="brutal-card bg-white p-2 text-sm flex items-start justify-between gap-2"
                  data-testid={`notes-history-item-${n.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{n.message}</div>
                    <div className="text-[11px] text-[color:var(--ink-soft)]">
                      {new Date(n.created_at).toLocaleString()}
                      {n.dismissed_at ? " · ✅ seen" : " · ⏳ unseen"}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
